import React, { useState } from 'react';
import { toast } from 'sonner';
import { Copy, Mail, UserMinus } from 'lucide-react';
import { axiosInstance as axios } from '../../../context/AuthContext';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { PROFESSIONAL_TYPES } from '../../pro/signup/professionalSignupConstants';

const TYPES = PROFESSIONAL_TYPES.map(t => [t.value, t.label]);
const EMPTY = { name: '', email: '', professional_type: 'financial_advisor', company: '', personal_note: '' };
const STATUS = { joined: 'Joined', pending: 'Invite sent', expired: 'Invite expired', none: 'No invite' };

const copy = async (link) => {
  try {
    await navigator.clipboard.writeText(link);
    toast.success('Invite link copied');
  } catch {
    window.prompt('Copy this invite link:', link);
  }
};

function ProRow({ p, onChanged }) {
  const remove = async () => {
    if (!window.confirm(`Remove ${p.name} from the pilot? Their listing stays as is.`)) return;
    try {
      await axios.delete(`/api/pilot/admin/professionals/${p.profile_id}`);
      onChanged();
    } catch {
      toast.error('Could not remove');
    }
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 py-3 border-b border-slate-700 last:border-0" data-testid={`pilot-pro-${p.profile_id}`}>
      <div>
        <p className="text-white font-medium">{p.name} <span className="text-slate-400 text-sm">· {p.professional_type?.replace(/_/g, ' ')}</span></p>
        <p className="text-slate-400 text-xs">
          {p.email} · {STATUS[p.invite_status] || p.invite_status} · Listing: {p.status}
          {' · '}{p.survey_submitted ? 'Feedback received' : 'No feedback yet'} · {p.notes_count} session note{p.notes_count === 1 ? '' : 's'}
        </p>
      </div>
      <div className="flex gap-2">
        {p.signup_link && (
          <Button size="sm" variant="outline" onClick={() => copy(p.signup_link)} data-testid="pilot-copy-link-btn">
            <Copy className="h-4 w-4 mr-1" />Copy invite link
          </Button>
        )}
        <Button size="sm" variant="ghost" className="text-red-300" onClick={remove} data-testid="pilot-remove-pro-btn">
          <UserMinus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export function PilotProsCard({ pros, onChanged }) {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const invite = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const r = await axios.post('/api/pilot/admin/invites', { ...form, company: form.company || null, personal_note: form.personal_note || null });
      toast.success(r.data.existing ? 'Added to the pilot and emailed' : 'Invitation emailed — use "Copy invite link" to share it another way');
      setForm(EMPTY);
      onChanged();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d[0]?.msg : d || 'Could not send invite');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-5" data-testid="pilot-pros-card">
      <h3 className="text-white font-semibold mb-1">Phase 1 — Pilot professionals</h3>
      <p className="text-slate-400 text-sm mb-4">They get a private link by email, set up their profile, accept the standards, and you approve them as usual.</p>
      <form onSubmit={invite} className="grid sm:grid-cols-2 gap-3 mb-4">
        <Input required placeholder="Full name" value={form.name} onChange={set('name')} className="bg-slate-900 border-slate-600 text-white" data-testid="pilot-invite-name" />
        <Input required type="email" placeholder="Email" value={form.email} onChange={set('email')} className="bg-slate-900 border-slate-600 text-white" data-testid="pilot-invite-email" />
        <select value={form.professional_type} onChange={set('professional_type')} className="rounded-md bg-slate-900 border border-slate-600 text-white px-3 py-2 text-sm" data-testid="pilot-invite-type">
          {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <Input placeholder="Company (optional)" value={form.company} onChange={set('company')} className="bg-slate-900 border-slate-600 text-white" data-testid="pilot-invite-company" />
        <Textarea placeholder="Personal note in the email (optional)" value={form.personal_note} onChange={set('personal_note')} className="sm:col-span-2 bg-slate-900 border-slate-600 text-white" data-testid="pilot-invite-note" />
        <Button type="submit" disabled={sending} className="sm:col-span-2 bg-emerald-600 hover:bg-emerald-700" data-testid="pilot-invite-submit">
          <Mail className="h-4 w-4 mr-2" />{sending ? 'Sending…' : 'Send pilot invitation'}
        </Button>
      </form>
      {pros.length === 0
        ? <p className="text-slate-500 text-sm" data-testid="pilot-pros-empty">No pilot professionals yet.</p>
        : pros.map(p => <ProRow key={p.profile_id} p={p} onChanged={onChanged} />)}
    </div>
  );
}
